//? Application API
import { api } from "../../services/apis/configs/axiosConfigs";

// ? Constants
import { API_URI } from "../../constants/consts";

// * Importing the existing Pagination type
import { type Pagination } from "../../services/stores/types";

//? Auxiliary types for relationships
import {
  type Application,
  type GetApplicationsResponse,
  type CreateApplicationInput,
  type ApplicationDetails,
  ApplicationCompleted,
} from "./types/types";


export const applicationAPI = {
  /**
   * ? Fetches all Applications with search and pagination support.
   *
   * @param search - Optional search term.
   * @param page - Page number for pagination.
   * @param token - Authentication token.
   * @param perPage - Number of items per page.
   * @returns A promise that resolves to GetApplicationsResponse.
   */
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<GetApplicationsResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/applications?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination || {
          currentPage: 0,
          totalPages: 0,
          itemsPerPage: 0,
          totalItems: 0,
        };

        return {
          applications: data.applications || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching applications:", error);
        throw new Error("Failed to fetch applications");
      });
  },

  /**
   * TODO: Fetches a specific Application by ID.
   *
   * @param id - Application ID.
   * @param token - Authentication token.
   * @returns A promise that resolves to ApplicationDetails.
   */
  getById: async function (
    id: string,
    token: string
  ): Promise<ApplicationDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/applications/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error fetching application:", error);
        throw new Error("Failed to fetch application");
      });
  },

  /**
   * ! Creates a new Application.
   *
   * @param data - Data to create the Application.
   * @param token - Authentication token.
   * @returns A promise that resolves to Application.
   */
  create: async function (
    data: CreateApplicationInput,
    token: string
  ): Promise<Application> {
    return api
      .request({
        url: `${API_URI}/admin/private/applications`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error creating application:", error, data);
        throw error;
      });
  },

  /**
   * ! Updates an existing Application.
   *
   * @param id - Application ID.
   * @param data - Data to update the Application.
   * @param token - Authentication token.
   * @returns A promise that resolves to Application.
   */
  update: async function (
    id: string,
    data: CreateApplicationInput,
    token: string
  ): Promise<Application> {
    return api
      .request({
        url: `${API_URI}/admin/private/applications/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating application:", error);
        throw new Error("Failed to update application");
      });
  },

  /**
   * ? Deletes a specific Application by ID.
   *
   * @param id - Application ID.
   * @param token - Authentication token.
   * @returns A promise that resolves to void.
   */
  delete: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/applications/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting application:", error);
        throw new Error("Failed to delete application");
      });
  },

  /**
   * Gera o diagrama e retorna os links dos PDFs gerados.
   *
   * @param id - ID da aplicação (applicationId)
   * @param diagramId - ID do diagrama a ser gerado
   * @param token - Token de autenticação
   * @returns Promise com um objeto contendo um array de PDFs
   */
  generate: async function (
    id: string,
    diagramId: string,
    answerSheetsId: string,
    token: string
  ): Promise<{ pdfs: string[] }> {

    console.log("generate", id, diagramId, answerSheetsId, token);

    const url = `${API_URI}/admin/private/applications/${id}/generate-diagram/${diagramId}/${answerSheetsId}`;
    return api
      .request({
        url,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error generating diagram:", error);
        throw new Error("Failed to generate diagram");
      });
  },

  getByFull: async function (
    id: string,
    diagramId: string,
    token: string,
    answerSheetsId?: string, // opcional
    studentId?: string // opcional
  ) {
    // Define a URL base com o prefixo "full"
    let url = `${API_URI}/admin/private/applications/full/${id}/${diagramId}`;
    // Se o answerSheetsId existir, inclua na URL
    if (answerSheetsId) {
      url += `/${answerSheetsId}`;
    }
    // Se houver studentId, adiciona como query param
    if (studentId) {
      url += `?student_id=${studentId}`;
    }

    console.log("url", url);

    return api
      .request({
        url: url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error fetching application:", error);
        throw new Error("Failed to fetch application");
      });
  },
};
